import Footer from '/web/shared/aifans/Footer'

import { documentToHtmlString } from '@contentful/rich-text-html-renderer'
import { contentfulClient, TypePolicySkeleton } from '/web/shared/aifans/lib/contentful-util'
import { createEffect, createSignal } from 'solid-js'
import { Title, Meta, Link } from '@solidjs/meta'
import { getCanonicalUrl } from '/web/shared/aifans/lib/config-util'

export default function AiFansPolicy(props: {
  contentId: string
  showSidebar?: boolean
  canonical: string
  notice?: string
  badge?: string
}) {
  const [title, setTitle] = createSignal('')
  const [metaTitle, setMetaTitle] = createSignal('')
  const [metaDescription, setMetaDescription] = createSignal('')
  const [body, setBody] = createSignal('')

  createEffect(async () => {
    const entry = await contentfulClient.getEntry<TypePolicySkeleton>(props.contentId)
    if (entry.fields.content) {
      setBody(documentToHtmlString(entry.fields.content))
    }
    if (entry.fields.title) {
      setTitle(entry.fields.title)
    }
    if (entry.fields.metaTitle) {
      setMetaTitle(entry.fields.metaTitle)
    }
    if (entry.fields.description) {
      setMetaDescription(entry.fields.description)
    }
  })

  return (
    <>
      <Title>{metaTitle()}</Title>
      <Meta name="og:title" content={metaTitle()} />
      <Meta name="description" content={metaDescription()} />
      <Meta name="og:description" content={metaDescription()} />
      <Link rel="canonical" href={getCanonicalUrl(props.canonical)} />

      <section
        class="mx-auto max-w-[960px]"
        style={{ padding: '32px clamp(16px, 3vw, 40px) 56px' }}
      >
        {/* Hero */}
        <div class="py-6 pb-8 text-center">
          <div class="mb-[14px] inline-flex items-center gap-2 rounded-full border border-[rgba(138,43,226,0.3)] bg-[rgba(138,43,226,0.12)] px-[14px] py-[6px] font-rajdhani text-[11px] font-bold uppercase tracking-[1.8px] text-[#FF3CAC]">
            <span class="h-[6px] w-[6px] rounded-full bg-[var(--cf-cyan)] shadow-[0_0_8px_var(--cf-cyan)]" />
            {props.badge || 'Legal'}
          </div>
          <h1 class="mb-[14px] font-bebas text-[clamp(36px,5.5vw,64px)] leading-none tracking-[1px] text-white">
            {title()}
          </h1>
          <p class="mx-auto max-w-[600px] font-rajdhani text-[15px] font-medium leading-[1.5] text-[var(--cf-muted)]">
            Please read this document carefully.
          </p>
        </div>

        <Show when={props.notice}>
          <div class="mb-8 flex justify-center">
            <div
              class="inline-flex items-center gap-[10px] rounded-full px-[22px] py-3 font-rajdhani text-[13px] font-bold tracking-[0.4px] text-[var(--cf-cyan)]"
              style={{
                background: 'rgba(0,229,255,0.08)',
                border: '1px solid rgba(0,229,255,0.3)',
              }}
            >
              <span class="text-[18px]">⏱</span>
              <span>{props.notice}</span>
            </div>
          </div>
        </Show>

        {/* Content body */}
        <div
          class="legal-content overflow-hidden rounded-[20px] border border-[var(--cf-hairline)] bg-[rgba(255,255,255,0.02)]"
          style={{ padding: 'clamp(24px, 4vw, 48px)' }}
        >
          <div
            class="font-nunito text-[14px] leading-[1.7] text-[rgba(255,255,255,0.72)]"
            innerHTML={body()}
          />
        </div>

        {/* CTA footer */}
        <div class="mt-9 border-t border-[rgba(255,255,255,0.06)] pt-7 text-center">
          <p class="mb-4 font-rajdhani text-[14px] font-semibold text-[var(--cf-muted)]">
            Have questions about this policy?
          </p>
          <div class="flex flex-wrap justify-center gap-3">
            <a
              href="/contact-us"
              class="inline-flex h-12 items-center justify-center gap-2 rounded-full px-6 font-rajdhani text-[14px] font-extrabold uppercase tracking-[0.8px] text-white transition-all duration-200 hover:translate-y-[-2px]"
              style={{
                background:
                  'linear-gradient(135deg, var(--cf-primary) 0%, #6a1fb8 50%, var(--cf-primary) 100%)',
                'background-size': '200% auto',
                'box-shadow': '0 6px 24px rgba(138,43,226,0.45)',
              }}
            >
              Contact Support
            </a>
            <a
              href="/faq"
              class="inline-flex h-12 items-center justify-center gap-2 rounded-full border border-[rgba(255,255,255,0.12)] bg-transparent px-6 font-rajdhani text-[14px] font-extrabold uppercase tracking-[0.8px] text-white transition-colors hover:border-[var(--cf-cyan)] hover:text-[var(--cf-cyan)]"
            >
              FAQ
            </a>
          </div>
        </div>
      </section>

      <Footer />

      <style>
        {`
          .legal-content p {
            font-family: 'Nunito', sans-serif;
            font-size: 14px;
            line-height: 1.7;
            color: rgba(255,255,255,0.72);
            margin-bottom: 12px;
          }
          .legal-content hr {
            border: none;
            height: 1px;
            background: rgba(138,43,226,0.2);
            margin: 28px 0;
          }
          .legal-content h2 {
            font-family: 'Bebas Neue', sans-serif;
            font-size: 26px;
            letter-spacing: 1px;
            color: #fff;
            margin-top: 28px;
            margin-bottom: 14px;
            line-height: 1.15;
          }
          .legal-content h3 {
            font-family: 'Rajdhani', sans-serif;
            font-weight: 700;
            font-size: 15px;
            letter-spacing: 0.4px;
            color: var(--cf-cyan);
            margin-top: 18px;
            margin-bottom: 8px;
          }
          .legal-content b, .legal-content strong {
            color: #fff;
            font-weight: 700;
          }
          .legal-content ul {
            list-style: none;
            margin: 10px 0 16px 20px;
            padding-left: 16px;
            border-left: 2px solid rgba(138,43,226,0.2);
          }
          .legal-content ol {
            list-style: decimal;
            margin: 10px 0 16px 20px;
            padding-left: 16px;
            border-left: 2px solid rgba(138,43,226,0.2);
          }
          .legal-content li {
            font-family: 'Nunito', sans-serif;
            font-size: 14px;
            line-height: 1.7;
            color: rgba(255,255,255,0.72);
            margin-bottom: 4px;
          }
          .legal-content li p {
            margin-bottom: 4px;
          }
          .legal-content a {
            color: var(--cf-cyan);
            text-decoration: underline;
          }
          .legal-content a:hover {
            color: #fff;
          }
        `}
      </style>
    </>
  )
}
